import React, { useEffect, useState } from 'react';
import api from '../../../utils/api';
import { useAuth } from '../../../context/AuthContext';
import { canManageCrmSalaries } from '../../../utils/crmPermissions';
import { Button } from '../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { useToast } from '../../../hooks/use-toast';

export default function CrmAdvanceManager() {
  const { user } = useAuth();
  const isAdmin = canManageCrmSalaries(user?.role_slug);
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [form, setForm] = useState({
    employee_id: '', amount: '', monthly_recovery_amount: '', notes: '',
  });

  const load = async () => {
    try {
      const [aRes, eRes] = await Promise.all([
        api.get('/crm/payroll/advances'),
        isAdmin ? api.get('/crm/employees') : Promise.resolve({ data: { data: { items: [] } } }),
      ]);
      setItems(aRes.data?.data?.items || []);
      setEmployees((eRes.data?.data?.items || []).filter((e) => e.status === 'active'));
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.message, variant: 'destructive' });
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const create = async () => {
    try {
      await api.post('/crm/payroll/advances', {
        employee_id: form.employee_id ? Number(form.employee_id) : undefined,
        amount: Number(form.amount),
        monthly_recovery_amount: Number(form.monthly_recovery_amount || form.amount),
        notes: form.notes,
      });
      toast({ title: 'Advance created' });
      setForm({ employee_id: '', amount: '', monthly_recovery_amount: '', notes: '' });
      load();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.message, variant: 'destructive' });
    }
  };

  const approve = async (id) => {
    try {
      await api.post(`/crm/payroll/advances/${id}/approve`, {});
      toast({ title: 'Approved' });
      load();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.message, variant: 'destructive' });
    }
  };

  const reject = async (id) => {
    try {
      await api.post(`/crm/payroll/advances/${id}/reject`, {});
      toast({ title: 'Rejected' });
      load();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.message, variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader><CardTitle className="text-base">Request / Create Advance</CardTitle></CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {isAdmin && (
            <div>
              <Label>Employee</Label>
              <Select value={form.employee_id} onValueChange={(v) => setForm((f) => ({ ...f, employee_id: v }))}>
                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  {employees.map((e) => (
                    <SelectItem key={e.id} value={String(e.id)}>
                      {e.first_name} {e.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label>Amount</Label>
            <Input type="number" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} />
          </div>
          <div>
            <Label>Monthly Recovery</Label>
            <Input type="number" value={form.monthly_recovery_amount} onChange={(e) => setForm((f) => ({ ...f, monthly_recovery_amount: e.target.value }))} />
          </div>
          <div>
            <Label>Notes</Label>
            <Input value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
          </div>
          <div className="flex items-end">
            <Button onClick={create}>Submit</Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Advances</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left">
                <th className="py-2">Employee</th>
                <th>Amount</th>
                <th>Remaining</th>
                <th>Recovery / mo</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <tr key={row.id} className="border-b">
                  <td className="py-2">{row.first_name} {row.last_name}</td>
                  <td>₹{Number(row.amount).toLocaleString('en-IN')}</td>
                  <td>₹{Number(row.remaining_balance).toLocaleString('en-IN')}</td>
                  <td>₹{Number(row.monthly_recovery_amount).toLocaleString('en-IN')}</td>
                  <td>{row.status}</td>
                  <td className="space-x-1">
                    {isAdmin && row.status === 'pending' && (
                      <>
                        <Button size="sm" onClick={() => approve(row.id)}>Approve</Button>
                        <Button size="sm" variant="outline" onClick={() => reject(row.id)}>Reject</Button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
